import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Phase } from '../data/mock';
import * as phaseService from '../services/phase-service';
import { PhaseProgressEntry } from '../services/phase-service';

interface PhaseContextValue {
  phases: Phase[];
  progress: Map<string, PhaseProgressEntry>;
  currentPhase: Phase | null;
  loading: boolean;
  allPhasesComplete: boolean;
  isPhaseUnlocked(phaseId: string): boolean;
  isPhaseComplete(phaseId: string): boolean;
  getPhaseProgress(phaseId: string): PhaseProgressEntry | null;
  refreshProgress(): Promise<void>;
  markPhaseComplete(phaseId: string): Promise<void>;
}

export const PhaseContext = createContext<PhaseContextValue | null>(null);

export const usePhases = () => {
  const ctx = useContext(PhaseContext);
  if (!ctx) throw new Error('usePhases must be used within PhaseProvider');
  return ctx;
};

export const PhaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [phases, setPhases] = useState<Phase[]>([]);
  const [progress, setProgress] = useState<Map<string, PhaseProgressEntry>>(new Map());
  const [loading, setLoading] = useState(true);

  const refreshProgress = useCallback(async () => {
    try {
      const data = await phaseService.getMyProgress();
      const map = new Map<string, PhaseProgressEntry>();
      if (Array.isArray(data)) {
        data.forEach(p => map.set(p.phaseId, p));
      }
      setProgress(map);
    } catch {
      // Keep existing progress on error
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const phaseData = await phaseService.getPhases();
        if (Array.isArray(phaseData) && phaseData.length > 0) {
          // Map _id → id for compatibility with Phase interface
          setPhases(phaseData.map((p: any) => ({ ...p, id: p._id || p.id })));
        }
      } catch {
        // No fallback — phases come from backend
      }
      await refreshProgress();
      setLoading(false);
    };
    load();
  }, [refreshProgress]);

  const isPhaseUnlocked = (phaseId: string): boolean => {
    const status = progress.get(phaseId)?.status;
    return status === 'active' || status === 'completed';
  };

  const isPhaseComplete = (phaseId: string): boolean =>
    progress.get(phaseId)?.status === 'completed';

  const getPhaseProgress = (phaseId: string): PhaseProgressEntry | null =>
    progress.get(phaseId) || null;

  const currentPhase = phases.find(p => progress.get(p.id)?.status === 'active') || null;

  const allPhasesComplete = phases.length > 0 && phases.every(p => isPhaseComplete(p.id));

  const markPhaseComplete = async (phaseId: string) => {
    await phaseService.markPhaseComplete(phaseId);
    await refreshProgress();
  };

  return (
    <PhaseContext.Provider value={{
      phases,
      progress,
      currentPhase,
      loading,
      allPhasesComplete,
      isPhaseUnlocked,
      isPhaseComplete,
      getPhaseProgress,
      refreshProgress,
      markPhaseComplete,
    }}>
      {children}
    </PhaseContext.Provider>
  );
};
